import { useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { virtualAccountsApi, type VirtualAccountDetail } from '@/api/virtualAccounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';

const NAME_MAX_LENGTH = 80;
const TITLE_ID = 'create-virtual-account-title';
const HELP_ID = 'create-virtual-account-help';

interface CreateVirtualAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (detail: VirtualAccountDetail) => void | Promise<void>;
  onSubscriptionRequired: () => void;
}

type FieldError = 'nameTaken' | null;

export function CreateVirtualAccountDialog({
  open,
  onClose,
  onCreated,
  onSubscriptionRequired,
}: CreateVirtualAccountDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<FieldError>(null);
  const [genericError, setGenericError] = useState(false);
  // The name field takes focus once the dialog has entered (`autoFocus` is a lint error here).
  const nameRef = useRef<HTMLInputElement>(null);
  const trimmed = name.trim();

  function reset() {
    setName('');
    setFieldError(null);
    setGenericError(false);
    setSubmitting(false);
  }

  const { dirty } = useDirty(name);
  // Closing (a clean close, or "Discard changes") empties the form for the next opening.
  const guard = useDialogDirtyGuard(
    dirty,
    () => {
      reset();
      onClose();
    },
    submitting,
    0,
    open,
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (trimmed.length === 0 || submitting) return;
    setSubmitting(true);
    setFieldError(null);
    setGenericError(false);
    try {
      const detail = await virtualAccountsApi.create({ name: trimmed });
      reset();
      await onCreated(detail);
    } catch (error) {
      const code = isApiError(error) ? error.code : ErrorCodes.UNKNOWN;
      if (code === 'VIRTUAL_ACCOUNT_NAME_TAKEN') {
        setFieldError('nameTaken');
      } else if (code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        reset();
        onSubscriptionRequired();
        return;
      } else {
        setGenericError(true);
      }
      guard.focusErrors();
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      open={open}
      titleId={TITLE_ID}
      aria-describedby={HELP_ID}
      maxWidth={false}
      slotProps={{
        transition: { onEntered: () => nameRef.current?.focus() },
        // The prototype's 460 (P:2007–2023).
        paper: { sx: { width: 460, maxWidth: 'calc(100% - 32px)' } },
      }}
    >
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <DialogTitle id={TITLE_ID}>{t('virtualAccounts.newTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText id={HELP_ID} sx={{ mb: 2 }}>
            {t('virtualAccounts.newHelp')}
          </DialogContentText>
          {genericError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('virtualAccounts.errorGeneric')}
            </Alert>
          )}
          <TextField
            label={t('virtualAccounts.nameLabel')}
            placeholder={t('virtualAccounts.namePlaceholder')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputRef={nameRef}
            required
            fullWidth
            error={fieldError !== null}
            helperText={
              fieldError === 'nameTaken' ? t('virtualAccounts.errorNameTaken') : undefined
            }
            slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" disabled={trimmed.length === 0} loading={submitting}>
            {t('virtualAccounts.create')}
          </SubmitButton>
        </DialogActions>
      </form>
    </GuardedDialog>
  );
}
